import React, { useState } from 'react'
import { ChevronDown, X } from 'lucide-react'
import { MENU } from '@/config/menu.config'
import { cn } from '@/lib/utils'
import { useLocation } from 'react-router-dom'

interface MobileDrawerProps {
  isOpen: boolean
  onClose: () => void
}

export function MobileDrawer({ isOpen, onClose }: MobileDrawerProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  const location = useLocation()

  const toggleSection = (label: string) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(label)) {
      newExpanded.delete(label)
    } else {
      newExpanded.add(label)
    }
    setExpandedSections(newExpanded)
  }

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/'
    return location.pathname.startsWith(href)
  }

  const handleLinkClick = () => {
    onClose()
  }

  return (
    <>
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden animate-in fade-in duration-200"
            onClick={onClose}
          />

          {/* Drawer */}
          <div
            className="fixed top-0 right-0 h-full w-full max-w-sm bg-background border-l shadow-2xl z-50 lg:hidden animate-in slide-in-from-right duration-300"
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-lg font-semibold">Menu</h2>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-muted rounded-lg transition-colors"
                  aria-label="Close menu"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Menu Content */}
              <div className="flex-1 overflow-y-auto p-4">
                <nav className="space-y-2">
                  {MENU.map((item, index) => (
                    <div
                      key={item.label}
                      className="animate-in fade-in slide-in-from-left duration-300"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      {item.children ? (
                        <div className="space-y-2">
                          <button
                            onClick={() => toggleSection(item.label)}
                            className={cn(
                              "flex items-center justify-between w-full p-3 rounded-lg text-left font-medium transition-all duration-200",
                              "hover:bg-muted",
                              expandedSections.has(item.label) && "text-primary bg-muted/50"
                            )}
                          >
                            <span>{item.label}</span>
                            <div
                              className="transition-transform duration-200"
                              style={{ transform: expandedSections.has(item.label) ? 'rotate(180deg)' : 'rotate(0deg)' }}
                            >
                              <ChevronDown className="w-4 h-4" />
                            </div>
                          </button>

                          {expandedSections.has(item.label) && (
                            <div
                              className="overflow-hidden animate-in slide-in-from-top duration-200"
                            >
                                <div className="pl-4 space-y-1">
                                  {item.label === 'Services' && item.children.map((group) => (
                                    'group' in group ? (
                                      <div key={group.group} className="space-y-1">
                                        <h4 className="text-xs font-semibold text-primary uppercase tracking-wide px-3 py-2">
                                          {group.group}
                                        </h4>
                                        {group.items?.map((subItem) => (
                                          <a
                                            key={subItem.label}
                                            href={subItem.href}
                                            onClick={handleLinkClick}
                                            className={cn(
                                              "flex items-center space-x-3 p-3 rounded-lg text-sm transition-colors",
                                              "hover:bg-muted/50",
                                              isActive(subItem.href) && "text-primary bg-muted/30"
                                            )}
                                          >
                                            {subItem.icon && <subItem.icon className="w-4 h-4" />}
                                            <span>{subItem.label}</span>
                                          </a>
                                        ))}
                                      </div>
                                    ) : (
                                      <a
                                        key={group.label}
                                        href={group.href}
                                        onClick={handleLinkClick}
                                        className={cn(
                                          "flex items-center space-x-3 p-3 rounded-lg text-sm transition-colors",
                                          "hover:bg-muted/50",
                                          isActive(group.href) && "text-primary bg-muted/30"
                                        )}
                                      >
                                        {group.icon && <group.icon className="w-4 h-4" />}
                                        <span>{group.label}</span>
                                      </a>
                                    )
                                  ))}
                                  
                                  {item.label === 'Sectors' && item.children.map((sector) => (
                                    'icon' in sector && (
                                      <a
                                        key={sector.label}
                                        href={sector.href}
                                        onClick={handleLinkClick}
                                        className={cn(
                                          "flex items-center space-x-3 p-3 rounded-lg text-sm transition-colors",
                                          "hover:bg-muted/50",
                                          isActive(sector.href) && "text-primary bg-muted/30"
                                        )}
                                      >
                                        {sector.icon && <sector.icon className="w-4 h-4" />}
                                        <span>{sector.label}</span>
                                      </a>
                                    )
                                  ))}
                                  
                                  {item.label === 'Company' && item.children.map((link) => (
                                    !('group' in link) && (
                                      <a
                                        key={link.label}
                                        href={link.href}
                                        onClick={handleLinkClick}
                                        className={cn(
                                          "block p-3 rounded-lg text-sm transition-colors",
                                          "hover:bg-muted/50",
                                          isActive(link.href) && "text-primary bg-muted/30"
                                        )}
                                      >
                                        {link.label}
                                      </a>
                                    )
                                  ))}
                                </div>
                              </div>
                            )}
                        </div>
                      ) : (
                        <a
                          href={item.href}
                          onClick={handleLinkClick}
                          className={cn(
                            "block p-3 rounded-lg font-medium transition-colors",
                            "hover:bg-muted",
                            isActive(item.href!) && "text-primary bg-muted/50"
                          )}
                        >
                          {item.label}
                        </a>
                      )}
                    </div>
                  ))}
                </nav>
              </div>

              {/* CTA Button */}
              <div className="p-6 border-t">
                <a
                  href="/contact"
                  onClick={handleLinkClick}
                  className={cn(
                    "block w-full text-center px-6 py-3 rounded-xl font-medium transition-all duration-300",
                    "bg-gradient-to-r from-primary to-blue-600 text-primary-foreground",
                    "hover:shadow-lg hover:shadow-primary/25 hover:scale-[1.02] active:scale-[0.98]"
                  )}
                >
                  Request a Demo
                </a>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}